import React from 'react';
import { Button, Box, Tooltip } from '@mui/material';
import { FileDownload, PictureAsPdf } from '@mui/icons-material';
import { useTenders } from '../../hooks/TenderContext';
import { formatDate } from '../../utils/helpers';
import ExcelJS from 'exceljs';

const ExportTools = () => {
  const { filteredTenders } = useTenders();

  const getExportData = () => {
    return filteredTenders.map(t => ({
      'מס"ד': t.id,
      'מספר מכרז': t.tender_number || '',
      'מזמין': t.client,
      'נושא המכרז': t.subject,
      'תחום': t.field || '',
      'סטטוס': t.status || '',
      'אופן זכייה': t.win_method || '',
      'מנהל': t.manager || '',
      'גורם מטפל': t.handler || '',
      'מפגש מציעים': formatDate(t.bidders_meeting),
      'שאלות הבהרה': formatDate(t.clarification_date),
      'מועד הגשה': formatDate(t.submission_date),
      'תוצאה': t.win_status || '',
      'תעדוף': t.priority || '',
      'הערות': t.notes || '',
    }));
  };

  const exportToExcel = async () => {
    const data = getExportData();
    const colWidths = [6, 12, 18, 35, 12, 10, 12, 12, 12, 14, 14, 14, 14, 8, 30];
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('מכרזים', { views: [{ rightToLeft: true }] });
    const keys = Object.keys(data[0] || {});
    ws.columns = keys.map((key, i) => ({ header: key, key, width: colWidths[i] || 12 }));
    data.forEach(row => ws.addRow(row));
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TMS_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    const headers = [
      'מס"ד', 'מספר מכרז', 'מזמין', 'נושא המכרז', 'תחום',
      'סטטוס', 'מועד הגשה', 'תוצאה', 'תעדוף',
    ];

    const rows = filteredTenders.map(t => [
      t.id,
      t.tender_number || '—',
      t.client,
      t.subject?.length > 50 ? t.subject.substring(0, 50) + '...' : t.subject,
      t.field || '—',
      t.status || '—',
      formatDate(t.submission_date),
      t.win_status || '—',
      t.priority || '—',
    ]);

    const today = new Date().toLocaleDateString('he-IL');

    const html = `
      <!DOCTYPE html>
      <html lang="he" dir="rtl">
      <head>
        <meta charset="utf-8">
        <title>TMS - דוח מכרזים</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;700&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Rubik', Arial, sans-serif;
            direction: rtl;
            padding: 30px;
            color: #212121;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 24px;
            padding-bottom: 16px;
            border-bottom: 3px solid #1565C0;
          }
          .header h1 { font-size: 22px; color: #1565C0; }
          .header .meta { font-size: 12px; color: #757575; text-align: left; }
          .logo { height: 40px; }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }
          th {
            background: #1565C0;
            color: white;
            padding: 10px 8px;
            text-align: right;
            font-weight: 600;
            white-space: nowrap;
          }
          td {
            padding: 8px;
            border-bottom: 1px solid #e0e0e0;
            text-align: right;
          }
          tr:nth-child(even) { background: #f5f5f5; }
          tr:hover { background: #e3f2fd; }
          .footer {
            margin-top: 20px;
            padding-top: 12px;
            border-top: 1px solid #e0e0e0;
            font-size: 10px;
            color: #999;
            text-align: center;
          }
          .summary {
            margin-bottom: 16px;
            font-size: 13px;
            color: #555;
          }
          @media print {
            body { padding: 15px; }
            table { font-size: 10px; }
            th { padding: 6px 4px; }
            td { padding: 5px 4px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>דוח מכרזים - TMS</h1>
            <div class="summary">סה"כ ${filteredTenders.length} מכרזים | תאריך הפקה: ${today}</div>
          </div>
          <div class="meta">
            <img src="/logo.png" class="logo" alt="Logo" onerror="this.style.display='none'" />
          </div>
        </div>
        <table>
          <thead>
            <tr>
              ${headers.map(h => `<th>${h}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${rows.map(row =>
              `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`
            ).join('')}
          </tbody>
        </table>
        <div class="footer">
          הופק אוטומטית ממערכת ניהול מכרזים TMS | ${today}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 500);
          };
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title="ייצוא ל-Excel">
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownload />}
          onClick={exportToExcel}
          color="success"
        >
          Excel
        </Button>
      </Tooltip>
      <Tooltip title="ייצוא ל-PDF">
        <Button
          variant="outlined"
          size="small"
          startIcon={<PictureAsPdf />}
          onClick={exportToPDF}
          color="error"
        >
          PDF
        </Button>
      </Tooltip>
    </Box>
  );
};

export default ExportTools;
